import { useTranslation } from 'react-i18next';

import Badge from '../../../../shared/components/Globals/Badge/Badge';
import FilterBar, { FilterPill, FilterPillMenu } from '../../../../shared/components/Globals/FilterBar/FilterBar';
import SearchBar from '../../../../shared/components/Globals/SearchBar/SearchBar';
import {
  PROSPECT_STATUS_BADGE,
  PROSPECT_STATUS_BADGE_VARIANT,
  PROSPECT_STATUS_LABEL,
  SKELETON_COUNT
} from '../../../../shared/constants/prospects';
import { IProspectListProps } from '../../../../shared/interfaces/prospects';
import { TRANSLATIONS_PROSPECTS } from '../../../../shared/translations/prospects';
import type { ProspectStatus } from '../../../../shared/types/prospects';
import './ProspectList.scss';

const ListItemSkeleton = () => (
  <div className="prospect-list__skeleton-item" aria-hidden="true">
    <div className="prospect-list__skeleton-row">
      <div className="prospect-list__skeleton-info">
        <span className="prospect-list__skeleton-bar prospect-list__skeleton-bar--name" />
        <span className="prospect-list__skeleton-bar prospect-list__skeleton-bar--sub" />
      </div>
      <span className="prospect-list__skeleton-badge" />
    </div>
  </div>
);

const ProspectList = ({
  prospects,
  loading,
  isSearching,
  query,
  onQueryChange,
  selectedId,
  onSelect,
  statusFilter,
  onStatusFilterChange
}: IProspectListProps) => {
  const { t } = useTranslation();

  const mainFilters: ProspectStatus[] = ['pending_quote', 'quoted', 'pending_activation'];
  const secondaryFilters: ProspectStatus[] = ['inactive', 'lost'];

  const renderContent = () => {
    if (loading || isSearching) {
      return (
        <div className="prospect-list__items-inner" aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <ListItemSkeleton key={i} />
          ))}
        </div>
      );
    }

    if (prospects.length === 0) {
      return (
        <div className="prospect-list__items-inner" aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <div key={i} className="prospect-list__skeleton-item prospect-list__skeleton-item--static">
              <div className="prospect-list__skeleton-row">
                <div className="prospect-list__skeleton-info">
                  <span className="prospect-list__skeleton-bar prospect-list__skeleton-bar--name prospect-list__skeleton-bar--static" />
                  <span className="prospect-list__skeleton-bar prospect-list__skeleton-bar--sub prospect-list__skeleton-bar--static" />
                </div>
                <span className="prospect-list__skeleton-badge prospect-list__skeleton-badge--static" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="prospect-list__items-inner">
        {prospects.map((prospect) => {
          const status =
            prospect.status in PROSPECT_STATUS_LABEL ? (prospect.status as ProspectStatus) : 'pending_quote';

          return (
            <button
              key={prospect.id}
              type="button"
              className={`prospect-list__item${selectedId === prospect.id ? ' prospect-list__item--active' : ''}`}
              onClick={() => onSelect(prospect.id)}
            >
              <div className="prospect-list__item-info">
                <span className="prospect-list__name">{prospect.companyName}</span>
                <div className="prospect-list__sub">
                  {prospect.workforceSize} {t(TRANSLATIONS_PROSPECTS.prospectsList.workforceSize)}
                </div>
              </div>
              <Badge variant={PROSPECT_STATUS_BADGE_VARIANT[status]}>{t(PROSPECT_STATUS_LABEL[status])}</Badge>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="prospect-list">
      <div className="prospect-list__header">
        <SearchBar
          value={query}
          onChange={onQueryChange}
          placeholder={t(TRANSLATIONS_PROSPECTS.prospectsList.searchPlaceholder)}
          isLoading={isSearching}
        />
        <FilterBar className="prospect-list__filters">
          {mainFilters.map((status) => (
            <FilterPill key={status} active={statusFilter === status} onClick={() => onStatusFilterChange(status)}>
              {t(PROSPECT_STATUS_BADGE[status].labelKey)}
            </FilterPill>
          ))}
          <FilterPillMenu
            label="..."
            active={statusFilter !== null && secondaryFilters.includes(statusFilter)}
            items={secondaryFilters.map((status) => ({
              key: status,
              label: t(PROSPECT_STATUS_BADGE[status].labelKey),
              active: statusFilter === status,
              onClick: () => onStatusFilterChange(statusFilter === status ? null : status)
            }))}
          />
        </FilterBar>
      </div>
      <div className="prospect-list__items">{renderContent()}</div>
    </div>
  );
};

export default ProspectList;
